import React from 'react';
import { YouTubeSearchResult, DrillFromYouTube, DrillDifficulty, AgeGroup } from './Model';
import { YouTubeEmbed } from './YouTubeEmbed';
import { getDifficultyColor, getAgeGroupColor } from './Drills/drillColors';
import { useReferenceData } from './ReferenceDataContext';

interface YouTubeSearchResultCardProps {
  result: YouTubeSearchResult;
  isSelected: boolean;
  selectedData?: DrillFromYouTube;
  onToggleSelection: (result: YouTubeSearchResult) => void;
  onUpdateSelection: (videoId: string, updates: Partial<DrillFromYouTube>) => void;
}

const formatCount = (count: number): string => {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K`;
  }
  return count.toString();
};

export const YouTubeSearchResultCard: React.FC<YouTubeSearchResultCardProps> = ({
  result,
  isSelected,
  selectedData,
  onToggleSelection,
  onUpdateSelection,
}) => {
  const { referenceData } = useReferenceData();
  const currentDifficulty = selectedData?.difficulty ?? result.inferredDifficulty;
  const currentAgeGroups = selectedData?.ageGroups ?? result.inferredAgeGroups;

  const toggleAgeGroup = (ageGroup: AgeGroup) => {
    const current = currentAgeGroups;
    const updated = current.includes(ageGroup)
      ? current.filter(ag => ag !== ageGroup)
      : [...current, ageGroup];
    onUpdateSelection(result.videoId, { ageGroups: updated });
  };

  return (
    <div className={`youtube-card ${isSelected ? 'youtube-card-selected' : ''}`}>
      {/* Header with checkbox */}
      <div
        className={`youtube-card-header ${isSelected ? 'youtube-card-header-selected' : ''}`}
        onClick={() => onToggleSelection(result)}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelection(result)}
          onClick={(e) => e.stopPropagation()}
          className="youtube-card-checkbox"
        />
        <div className="flex-1 min-w-0">
          <h4 className="youtube-card-title" title={result.title}>
            {result.title}
          </h4>
        </div>
      </div>

      {/* Video embed */}
      <div className="youtube-card-video">
        <YouTubeEmbed url={result.youtubeUrl} title={result.title} borderRadius={4} />
      </div>

      {/* Stats row */}
      <div className="youtube-card-stats">
        <span title="Views">
          <span className="youtube-card-stat-icon">&#128065;</span>
          {formatCount(result.viewCount)}
        </span>
        <span title="Likes">
          <span className="youtube-card-stat-icon">&#128077;</span>
          {formatCount(result.likeCount)}
        </span>
        <span title="Comments">
          <span className="youtube-card-stat-icon">&#128172;</span>
          {formatCount(result.commentCount)}
        </span>
        <span title="Duration" className="ml-auto">
          {result.duration} min
        </span>
      </div>

      {/* Description */}
      <div className="youtube-card-description" title={result.description}>
        {result.description}
      </div>

      {/* Inline editing (only when selected) */}
      {isSelected && (
        <div className="youtube-card-edit">
          {/* Difficulty dropdown */}
          <div className="mb-3">
            <label className="form-label text-sm">Difficulty</label>
            <select
              value={currentDifficulty}
              onChange={(e) =>
                onUpdateSelection(result.videoId, {
                  difficulty: e.target.value as DrillDifficulty,
                })
              }
              className="form-select w-full"
              style={{
                backgroundColor: getDifficultyColor(currentDifficulty).bg,
                color: getDifficultyColor(currentDifficulty).text,
              }}
            >
              {(referenceData?.difficulties ?? []).map((diff) => (
                <option key={diff} value={diff}>
                  {diff}
                </option>
              ))}
            </select>
          </div>

          {/* Age groups checkboxes */}
          <div>
            <label className="form-label text-sm">Age Groups</label>
            <div className="flex flex-wrap gap-2">
              {(referenceData?.ageGroups ?? []).map((ag) => {
                const isChecked = currentAgeGroups.includes(ag.value);
                const colors = getAgeGroupColor(ag.value);
                return (
                  <label
                    key={ag.value}
                    className="flex items-center gap-1 p-2 rounded cursor-pointer text-sm"
                    style={{
                      backgroundColor: isChecked ? colors.bg : '#ffffff',
                      border: `1px solid ${isChecked ? colors.border : '#ced4da'}`,
                      color: isChecked ? colors.text : '#6c757d',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleAgeGroup(ag.value)}
                      className="cursor-pointer"
                    />
                    {ag.value}
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Inferred tags (when not selected, show what would be applied) */}
      {!isSelected && (
        <div className="youtube-card-tags">
          <span
            className="youtube-tag"
            style={{
              backgroundColor: getDifficultyColor(result.inferredDifficulty).bg,
              color: getDifficultyColor(result.inferredDifficulty).text,
            }}
          >
            {result.inferredDifficulty}
          </span>
          {result.inferredAgeGroups.map((ag) => (
            <span
              key={ag}
              className="youtube-tag"
              style={{
                backgroundColor: getAgeGroupColor(ag).bg,
                color: getAgeGroupColor(ag).text,
              }}
            >
              {ag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
